import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { tracingAxios } from 'Repository/Sentry.js'
import { Environment } from 'environment.js'
import { addMessage } from '../../../Flux/Slice/Application/ApplicationSlice.js'
import { errorMessage } from '../../../Model/Message/IMessage.js'
import { calculatePrice, formatPrice } from './billingUtils.js'
import { trackBilling } from '../../../Services/Telemetry/index.js'
import type { CreditBalanceDTO } from '../../../Generated/CreditBalanceDTO.ts'
import type { CreditItemDTO } from '../../../Generated/CreditItemDTO.ts'
import type { CheckoutResponse } from '../../../Generated/CheckoutResponse.ts'
import { DateWidget } from '../../Common/DateWidget.js'

interface CreditBalanceCardProps {
  balance: CreditBalanceDTO | null
  creditItem: CreditItemDTO | null
}

export const CreditBalanceCard = ({ balance, creditItem }: CreditBalanceCardProps) => {
  const dispatch = useDispatch()
  const [quantity, setQuantity] = useState(10)
  const [loading, setLoading] = useState(false)

  const handleRecharge = async () => {
    if (!creditItem || quantity <= 0) return
    setLoading(true)
    trackBilling('recharge_initiated', { quantity })
    try {
      const { data } = await tracingAxios.post<CheckoutResponse>(
        `${Environment.API_URL}/v1/chargebee/recharge`,
        {
          sampleCreditItemPriceId: creditItem.id,
          sampleQuantity: quantity,
          redirectUrl: `${window.location.origin}/billing?recharge=success`
        },
        { withCredentials: true }
      )
      window.location.href = data.url
    } catch (e) {
      console.error('Recharge failed', e)
      dispatch(addMessage(errorMessage('Credit recharge failed. Please try again or contact support.')))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div data-testid="credit-balance-card" className="bg-slate-100 rounded-lg p-6 drop-shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Balance */}
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Sample Credits</h2>
            {creditItem && (creditItem.tiers?.length ?? 0) > 0 && (
              <div className="dropdown dropdown-bottom">
                <div tabIndex={0} role="button" className="cursor-pointer">
                  <svg className="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div tabIndex={0} className="dropdown-content z-10 bg-base-100 rounded-lg shadow-lg p-3 w-56">
                  <p className="text-xs font-medium text-gray-700 mb-1.5">Tier Pricing</p>
                  <div className="text-xs text-gray-500 space-y-0.5">
                    {creditItem.tiers?.map((tier, i) => (
                      <div key={i}>
                        {tier.endingUnit != null ? `${tier.startingUnit}\u2013${tier.endingUnit} credits` : `${tier.startingUnit}+ credits`}
                        {' \u2014 '}
                        {formatPrice(tier.priceInCents, creditItem.currencyCode)}/credit
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-baseline gap-2 mt-1">
            <span data-testid="credit-balance-value" className="text-4xl font-bold tabular-nums">
              {balance?.balance ?? 0}
            </span>
            <span className="text-sm text-gray-500">credits available</span>
          </div>
          {balance?.lastUpdated && (
            <p className="text-xs text-gray-400 mt-1">Last updated: <DateWidget date={balance.lastUpdated} /></p>
          )}
        </div>

        {/* Recharge controls */}
        {creditItem && (
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="number"
              min={1}
              step={1}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              className="input input-bordered input-sm w-24"
            />
            <span className="text-sm text-gray-500">{formatPrice(calculatePrice(creditItem, quantity), creditItem.currencyCode)}</span>
            <button className="btn btn-primary btn-sm" onClick={handleRecharge} disabled={loading || quantity <= 0}>
              {loading ? <span className="loading loading-spinner loading-sm" /> : 'Recharge Credits'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
